import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Users, UserPlus, UserCheck, CheckCircle, X } from "lucide-react";
import { BACKEND_URL } from "./networkConstants";

export function MyNetworkTab({ user }) {
  const [connections, setConnections] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchConnections(); }, []);

  const fetchConnections = async () => {
    try {
      const [connRes, pendRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/network/connections`,         { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/network/connections/pending`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setConnections(connRes.data || []);
      setPending(pendRes.data || []);
    } catch { }
    finally { setLoading(false); }
  };

  const respond = async (requestId, accept) => {
    try {
      await axios.post(`${BACKEND_URL}/api/network/connections/${requestId}/${accept ? 'accept' : 'reject'}`, {}, { withCredentials: true });
      setPending(p => p.filter(r => r.request_id !== requestId));
      if (accept) fetchConnections();
      toast.success(accept ? "Connection accepted!" : "Request declined");
    } catch { toast.error("Action failed"); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 text-white/20 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {pending.length > 0 && (
        <div>
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Pending Requests ({pending.length})</p>
          <div className="space-y-2">
            {pending.map(req => (
              <div key={req.request_id || req._id} className="bg-[#0D0D0D] border border-[#D4AF37]/15 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/20 flex items-center justify-center font-bold text-[#D4AF37] text-sm flex-shrink-0">
                  {(req.from_name || "A")[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{req.from_name || "Network Member"}</p>
                  <p className="text-white/30 text-xs">wants to connect</p>
                </div>
                <button onClick={() => respond(req.request_id, true)} className="p-2 text-[#00FF88] hover:bg-emerald-500/10 rounded-xl transition-all">
                  <CheckCircle className="w-4 h-4" />
                </button>
                <button onClick={() => respond(req.request_id, false)} className="p-2 text-[#FF3B30] hover:bg-red-500/10 rounded-xl transition-all">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Your Connections ({connections.length})</p>
        {connections.length === 0 ? (
          <div className="text-center py-12 bg-[#0A0A0A] border border-dashed border-white/[0.06] rounded-2xl">
            <div className="w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
              <Users className="w-6 h-6 text-white/15" />
            </div>
            <p className="text-white/40 text-sm font-medium">No connections yet</p>
            <p className="text-white/20 text-xs mt-1">Browse Partners to find and connect</p>
            <a href="/network?tab=partners"
              className="inline-flex items-center gap-1.5 mt-4 text-xs text-[#D4AF37] border border-[#D4AF37]/20 px-4 py-2 rounded-lg hover:bg-[#D4AF37]/[0.06] transition-all">
              <UserPlus className="w-3 h-3" /> Find Partners
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {connections.map(conn => (
              <div key={conn.user_id} className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4 flex items-center gap-3 hover:border-white/[0.1] transition-all">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/20 flex items-center justify-center font-bold text-emerald-400 text-sm flex-shrink-0">
                  {(conn.name || "A")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{conn.name}</p>
                  {conn.organization_name && <p className="text-white/30 text-xs truncate">{conn.organization_name}</p>}
                </div>
                <UserCheck className="w-4 h-4 text-[#00FF88] flex-shrink-0" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
